import type { FC } from 'react'
import React, { memo, useMemo, useState } from 'react'
import { makeStyles, createStyles } from '@material-ui/core/styles'
import { isEqual } from '@homeviews/utils'
import { Box } from '@material-ui/core'
import CsvExportButton from '../CsvExportButton'
import {
  useGetDevelopmentsBenchmarkQuery,
  useGetAdditionalQuestionsBenchmarkQuery,
  useGetDevelopmentsStatsQuery,
  isEngageUser,
} from '@homeviews/store'
import { createDevelopmentScoresTableRows } from '../../helpers/tableResolvers'
import { prepareDevelopmentScoresTableCsvData } from '../../helpers/transform'
import { Select, Table, Typography } from '@homeviews/shared-ui'
import { useSelector } from 'react-redux'
import { mixpanelTrack } from '@homeviews/seo'

const datasets = {
  reviewQuestions: {
    headCells: [
      {
        id: 'name',
        label: '',
        width: 400,
      },
      {
        id: 'rating',
        label: 'Overall',
      },
      {
        id: 'rating_facilities',
        label: 'Facilities',
      },
      {
        id: 'rating_location',
        label: 'Location',
      },
      {
        id: 'rating_value',
        label: 'Value',
      },
      {
        id: 'rating_design',
        label: 'Design',
      },
      {
        id: 'rating_management',
        label: 'Management',
      },
    ],
  },
  btsAdditionalQuestions: {
    headCells: [
      {
        id: 'name',
        label: '',
      },
      {
        id: 'owner_rating_service',
        label: 'Customer Service',
      },
      {
        id: 'owner_rating_timetable',
        label: 'Delivery Timetable',
      },
      {
        id: 'owner_rating_quality',
        label: 'Delivery Quality',
      },
      {
        id: 'owner_rating_aftersales',
        label: 'After Sales Care',
      },
    ],
  },
  btrAdditionalQuestions: {
    headCells: [
      {
        id: 'name',
        label: '',
      },
      {
        id: 'tenant_rating_service',
        label: 'Customer Service',
      },
      {
        id: 'tenant_rating_repairs',
        label: 'Repairs and Maintenance',
      },
      {
        id: 'tenant_rating_additional',
        label: 'Additional Services',
      },
    ],
  },
}

const useStyles = makeStyles(() =>
  createStyles({
    root: {
      '& .MuiTableCell-head': {
        textAlign: 'center',
        '&:first-child': {
          textAlign: 'left',
        },
      },
      '& .c-tooltip': {
        marginLeft: 5,
      },
      '& .c-tooltip__icon': {
        color: `#000433 !important`,
      },
    },
    filters: {
      display: 'flex',
      flexWrap: 'wrap',
      marginLeft: 'auto',
    },
    filtersWrapper: {
      display: 'flex',
      marginBottom: 19,
    },
    header: {},
    selectWrapper: {
      width: 205,
      fontWeight: 500,
    },
  }),
)
interface IProps {
  activeFilters: any
  graphDisabled?: boolean
  gridRef?: HTMLElement
  filtersDetails: any
  singleDevelopmentScoresChart?: any
  appliedFiltersData?: any
  developmentType?: any
}

const DevelopmentScoresTableContainer: FC<IProps> = memo(
  ({
    activeFilters,
    graphDisabled,
    filtersDetails,
    appliedFiltersData,
    developmentType,
  }) => {
    const classes = useStyles()

    const [typeFilter, setTypeFilter] = useState<string | number>(
      'reviewQuestions',
    )

    const isEngagePackage = Boolean(useSelector(isEngageUser))

    const { data: developmentsScores } = useGetDevelopmentsStatsQuery(
      {
        ...activeFilters,
        engage: isEngagePackage,
      },
      {
        skip: graphDisabled,
      },
    )

    const { data: developmentsBenchmarkData } =
      useGetDevelopmentsBenchmarkQuery(
        {
          ...activeFilters,
          engage: isEngagePackage,
        },
        {
          skip: graphDisabled,
        },
      )

    const { data: additionalQuestionsBenchmarkData } =
      useGetAdditionalQuestionsBenchmarkQuery(
        {
          ...activeFilters,
        },
        {
          skip: graphDisabled,
        },
      )

    const headCells = useMemo(
      () => datasets?.[typeFilter]?.headCells || [],
      [typeFilter],
    )
    const { rows, benchmarkRow } = useMemo(
      () =>
        createDevelopmentScoresTableRows(
          developmentsScores?.data,
          developmentsBenchmarkData?.data,
          typeFilter === 'btrAdditionalQuestions' ||
            typeFilter === 'btsAdditionalQuestions'
            ? additionalQuestionsBenchmarkData?.data
            : null,
          headCells,
          typeFilter,
          developmentType,
        ),
      [
        developmentsScores?.data,
        developmentsBenchmarkData?.data,
        headCells,
        typeFilter,
        developmentType,
      ],
    )

    const hasBtrDevelopments = useMemo(() => {
      return checkHasBtrDevelopments()
    }, [checkHasBtrDevelopments, developmentsScores?.data])

    const hasBtsDevelopments = useMemo(() => {
      return checkHasBtsDevelopments()
    }, [developmentsBenchmarkData, developmentsScores?.data])

    const handleFilterChange = (option: {
      value: string | number
      label: 'string'
    }) => {
      setTypeFilter(option?.value || '')

      mixpanelTrack('DASHBOARD_CHART_FILTER_CHANGE', {
        'Chart Name': 'Selected development scores',
        Filter: option?.label || '',
      })
    }

    const handleExportCsvButtonClick = (callback) => {
      if (!callback) {
        return
      }

      const exportData = prepareDevelopmentScoresTableCsvData(
        headCells,
        rows,
        benchmarkRow,
        appliedFiltersData,
      )
      callback(exportData)
    }

    function checkHasBtrDevelopments() {
      return developmentsScores?.data?.filter?.(
        (development: any) =>
          development.development_type?.name === 'Build To Rent' &&
          development.tenant_rating_additional !== '0.00' &&
          development.tenant_rating_repairs !== '0.00' &&
          development.tenant_rating_service !== '0.00',
      )?.length
    }

    function checkHasBtsDevelopments() {
      return developmentsScores?.data?.filter?.((development: any) => {
        return (
          development.development_type?.name !== 'Build To Rent' &&
          development.owner_rating_service !== '0.00' &&
          development.owner_rating_timetable !== '0.00' &&
          development.owner_rating_quality !== '0.00' &&
          development.owner_rating_aftersales !== '0.00'
        )
      })?.length
    }

    const typeFilterOptions = [
      {
        label: 'Review Questions',
        value: 'reviewQuestions',
      },
    ]

    if (hasBtrDevelopments) {
      typeFilterOptions.push({
        label: 'BTR Additional Questions',
        value: 'btrAdditionalQuestions',
      })
    } else {
      if (typeFilter === 'btrAdditionalQuestions') {
        setTypeFilter('reviewQuestions')
      }
    }

    if (hasBtsDevelopments) {
      typeFilterOptions.push({
        label: 'BTS Additional Questions',
        value: 'btsAdditionalQuestions',
      })
    } else {
      if (typeFilter === 'btsAdditionalQuestions') {
        setTypeFilter('reviewQuestions')
      }
    }

    // if (!rows || !rows?.length) {
    //   if (gridRef) {
    //     gridRef.style.display = 'none'
    //   }
    //   return <NoData />
    // } else {
    //   if (gridRef) {
    //     gridRef.style.display = 'block'
    //   }
    // }

    return (
      <div className={classes.root}>
        <Box
          className={classes.header}
          justifyContent="space-between"
          display="flex"
          mb={2}
        >
          <div className="h-exportableDataTitle">
            <Typography variant="h3">Selected development scores</Typography>
            <CsvExportButton onClick={handleExportCsvButtonClick} />
          </div>

          <div className={classes.selectWrapper}>
            <Select
              options={typeFilterOptions}
              value={typeFilter}
              onChange={handleFilterChange}
              alignRight
            />
          </div>
        </Box>
        {filtersDetails && <Box mb={3}>{filtersDetails}</Box>}
        <Table
          headCells={headCells}
          rows={rows}
          stickyRows={[benchmarkRow]}
          layout="benchmarkTable"
          defaultSort={{
            orderByIndex: 2,
            order: 'desc',
          }}
          paginated
          sortable
        />
      </div>
    )
  },
  (prevProps, nextProps) => isEqual(prevProps, nextProps),
)

export default DevelopmentScoresTableContainer
